<script setup lang="ts">
import { computed } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import SidebarUserMenu from '@/components/layout/SidebarUserMenu.vue'
import ProjectSidebarNav from '@/components/layout/ProjectSidebarNav.vue'
import CustomerSidebarContent from '@/components/layout/CustomerSidebarContent.vue'
import AppHeader from '@/components/layout/AppHeader.vue'
import SidebarCollapseToggle from '@/components/layout/SidebarCollapseToggle.vue'
import AppErrorNotifications from '@/components/common/AppErrorNotifications.vue'
import UIVersionWatcher from '@/components/common/UIVersionWatcher.vue'
import { useSidebarStore } from '@/stores/sidebar'
import { useNavRefresh } from '@/composables/useNavRefresh'
import { useIsExtractionView } from '@/composables/useIsExtractionView'

const sidebar = useSidebarStore()
const route = useRoute()
const navRefresh = useNavRefresh()

const isCustomerPortal = computed(() => !!route.meta.customerPortal)

// `project-detail` is the parent shell that redirects to `project-overview`;
// the project-scoped sidebar nav is conditioned on the active sub-route name.
const isProjectRoute = computed(() => {
  const name = typeof route.name === 'string' ? route.name : ''
  return name.startsWith('project-')
})
const isExtractionView = useIsExtractionView()
const hasWorkbookIdentity = computed(
  () => typeof route.query.workbook_id === 'string' || typeof route.query.path === 'string',
)
// Full-bleed when the view owns its own workspace chrome. Other routes keep
// the default page padding.
const isWorkbookDetailView = computed(
  () => route.name === 'project-review-workbook' && hasWorkbookIdentity.value,
)
const isCustomerWorkbookDetail = computed(
  () => route.name === 'customer-workbooks' && hasWorkbookIdentity.value,
)
const isFullBleedView = computed(
  () =>
    isExtractionView.value ||
    Boolean(route.meta.isFileWorkspace) ||
    isWorkbookDetailView.value ||
    isCustomerWorkbookDetail.value,
)

// Force the sidebar into rail mode whenever there is no project context to
// show. The expanded width only earns its keep when ProjectSidebarNav is
// rendering tab labels; on the Projects list, Settings, Tasks, and Admin
// views, the rail (notifications + menu + user) is all that displays. The
// user's saved collapse preference is preserved — it still applies when they
// return to a project.
const forceCollapsed = computed(() => !isProjectRoute.value && !isCustomerPortal.value)
const isCollapsed = computed(() => sidebar.isCollapsed || forceCollapsed.value)

const sidebarStyle = computed(() => (isCollapsed.value ? {} : { width: `${sidebar.width}px` }))
</script>

<template>
  <div class="layout">
    <AppHeader />

    <div class="layout__body">
      <aside
        class="sidebar"
        :class="{ 'sidebar--collapsed': isCollapsed }"
        :style="sidebarStyle"
        :aria-label="isCollapsed ? 'Sidebar (collapsed)' : 'Sidebar'"
      >
        <div class="sidebar__inner">
          <SidebarCollapseToggle v-if="!forceCollapsed && !isCustomerPortal" />
          <CustomerSidebarContent v-if="isCustomerPortal" :collapsed="isCollapsed" />
          <ProjectSidebarNav v-else-if="isProjectRoute" />
          <!-- Global nav lives in the SidebarUserMenu "Menu" popover; no inline fallback.
               Pass the effective collapse (store OR forced on non-project routes) so the
               rail's rows match the aside's actual width. -->
          <div v-if="!isCustomerPortal" class="sidebar__user-wrap">
            <SidebarUserMenu :collapsed="isCollapsed" />
          </div>
        </div>
      </aside>

      <main class="content" :class="{ 'content--full-bleed': isFullBleedView }">
        <RouterView :key="navRefresh.key.value" />
      </main>
    </div>

    <UIVersionWatcher />
    <AppErrorNotifications />
  </div>
</template>

<style scoped>
.layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--bg-darkest);
  overflow: hidden;
}

.layout__body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.sidebar {
  position: relative;
  height: 100%;
  width: var(--sidebar-width);
  flex-shrink: 0;
  background: var(--bg-dark);
  border-right: var(--space-px) solid var(--border);
  display: flex;
  flex-direction: column;
  padding: var(--space-4) var(--space-3);
  overflow: visible;
  transition: width var(--duration-slow) var(--ease-default);
}

.sidebar--collapsed {
  width: var(--sidebar-width-collapsed);
  padding: var(--space-4) var(--space-2);
}

.sidebar__inner {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

@media (prefers-reduced-motion: reduce) {
  .sidebar {
    transition: none;
  }
}

.sidebar__user-wrap {
  margin-top: auto;
  flex-shrink: 0;
}

.content {
  flex: 1;
  min-width: 0;
  /* Flex column so children with `flex: 1; min-height: 0;` can fill the column. */
  display: flex;
  flex-direction: column;
  padding: var(--space-6) var(--space-8);
  overflow: auto;
}

.content--full-bleed {
  padding: 0;
  overflow: hidden;
}
</style>
